import { useState } from 'react';

import { usePlayer } from '../../stores/player';

/**
 * The track's video, from YouTube, on the track page.
 *
 * ## A picture until it is asked for
 *
 * YouTube's embed is half a megabyte of script and a handful of cookies, and
 * loading it for every visitor of a page most of them came to for the audio
 * is a poor trade. So the page shows the video's own thumbnail with a play
 * button, and the real player is created the moment somebody presses it,
 * already playing. The privacy-enhanced host is used for the frame, which
 * sets nothing until the video is actually played.
 *
 * ## One thing playing at a time
 *
 * Pressing play here pauses the site's own player if it was running. A
 * track page playing its audio under its own video is the one outcome
 * nobody wants, and the video cannot be told to wait.
 */
export function TrackVideo({
  video,
  title,
}: {
  video: { provider: string; id: string; url: string };
  title: string;
}) {
  const [loaded, setLoaded] = useState(false);
  const playing = usePlayer((s) => s.playing);
  const toggle = usePlayer((s) => s.toggle);

  if (video.provider !== 'youtube') return null;

  function start() {
    if (playing) toggle();

    setLoaded(true);
  }

  return (
    <div
      className="relative aspect-video w-full overflow-hidden rounded-[var(--radius-card)] border border-line bg-black"
      data-track-video={video.id}
    >
      {loaded ? (
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${video.id}?autoplay=1&rel=0`}
          title={`${title} on YouTube`}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="absolute inset-0 h-full w-full"
        />
      ) : (
        <button
          type="button"
          onClick={start}
          aria-label={`Play the video for ${title}`}
          className="group absolute inset-0 h-full w-full"
          data-track-video-play
        >
          <img
            src={`https://i.ytimg.com/vi/${video.id}/hqdefault.jpg`}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
          />
          <span className="absolute inset-0 bg-black/20 transition-colors group-hover:bg-black/10" />
          <span className="absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-[var(--radius-pill)] bg-accent text-accent-ink shadow-lg transition-transform group-hover:scale-105">
            <svg viewBox="0 0 16 16" className="h-7 w-7 translate-x-0.5" fill="currentColor" aria-hidden="true">
              <path d="M4.5 2.8v10.4a.6.6 0 0 0 .93.5l8-5.2a.6.6 0 0 0 0-1l-8-5.2a.6.6 0 0 0-.93.5Z" />
            </svg>
          </span>
          <span className="meta-caps absolute bottom-3 right-3 rounded-[var(--radius-chip)] bg-black/60 px-2 py-1 text-white">
            YouTube
          </span>
        </button>
      )}
    </div>
  );
}
